import React, { useState, useMemo } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  TextField,
  InputAdornment,
} from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SearchIcon from "@material-ui/icons/Search";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  sonarContainer: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  sonarBeacon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText || "#ffffff",
    position: "relative",
    isolation: "isolate",
    transform: "translateZ(0)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark || theme.palette.primary.main,
      transform: "scale(1.08) translateZ(0)",
      boxShadow: `0 0 12px ${theme.palette.primary.main}E6`,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: "50%",
      border: `2px solid ${theme.palette.primary.main}`,
      animation: "$sonarPulse 2s infinite cubic-bezier(0.24, 0, 0.38, 1)",
      pointerEvents: "none",
      willChange: "transform, opacity",
      zIndex: -1,
    },
  },
  "@keyframes sonarPulse": {
    "0%": {
      transform: "scale(1)",
      opacity: 0.85,
    },
    "70%": {
      transform: "scale(1.45)",
      opacity: 0,
    },
    "100%": {
      transform: "scale(1.45)",
      opacity: 0,
    },
  },
  drawerPaper: {
    width: 520,
    maxWidth: "92vw",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.type === "dark" ? "#111827" : "#fcfcfd",
    color: theme.palette.text.primary,
    boxShadow: "-8px 0 24px rgba(0, 0, 0, 0.25)",
  },
  drawerHeader: {
    padding: "20px 24px 16px 24px",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
        : "linear-gradient(135deg, #f0fdf4 0%, #e6fffa 100%)",
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
  },
  badgeChip: {
    backgroundColor: `${theme.palette.primary.main}26`,
    color: theme.palette.type === "dark" ? (theme.palette.primary.light || theme.palette.primary.main) : theme.palette.primary.dark,
    fontWeight: 700,
    fontSize: "0.75rem",
    borderRadius: 6,
    height: 24,
    marginBottom: 8,
  },
  drawerTitle: {
    fontWeight: 700,
    fontSize: "1.15rem",
    lineHeight: 1.3,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#0f172a",
  },
  searchBox: {
    padding: "12px 24px",
    backgroundColor: theme.palette.type === "dark" ? "#1e293b" : "#ffffff",
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)"
    }`,
  },
  drawerContent: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 20px 32px 20px",
  },
  summaryCard: {
    padding: "16px",
    marginBottom: "20px",
    borderRadius: "10px",
    backgroundColor: theme.palette.type === "dark" ? "#1e293b" : "#f8fafc",
    border: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  summaryText: {
    fontSize: "0.88rem",
    lineHeight: 1.65,
    textAlign: "justify",
    textJustify: "inter-word",
    color: theme.palette.text.primary,
  },
  accordionRoot: {
    marginBottom: "12px",
    borderRadius: "8px !important",
    backgroundColor: theme.palette.type === "dark" ? "#1e293b" : "#ffffff",
    border: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: "0 0 12px 0",
    },
  },
  expanded: {},
  accordionSummary: {
    padding: "0 16px",
    minHeight: 52,
    "&$expanded": {
      minHeight: 52,
      borderBottom: `1px solid ${
        theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)"
      }`,
    },
    "& .MuiAccordionSummary-content": {
      margin: "12px 0",
      width: "calc(100% - 32px)",
    },
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.type === "dark" ? "#f1f5f9" : "#1e293b",
  },
  accordionDetails: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  itemRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    width: "100%",
  },
  iconBadge: {
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: `${theme.palette.primary.main}20`,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}33`,
    marginTop: 2,
  },
  textContentColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
  },
  itemTitle: {
    fontWeight: 700,
    fontSize: "0.92rem",
    lineHeight: 1.35,
    marginBottom: 4,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#0f172a",
  },
  labelTag: {
    fontWeight: 700,
    fontSize: "0.74rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginTop: 4,
    marginBottom: 2,
    color: theme.palette.type === "dark" ? "#94a3b8" : "#64748b",
  },
  justifiedParagraph: {
    fontSize: "0.85rem",
    lineHeight: 1.65,
    textAlign: "justify",
    textJustify: "inter-word",
    color: theme.palette.type === "dark" ? "#cbd5e1" : "#334155",
    marginBottom: 6,
  },
  boxImportance: {
    marginTop: 6,
    padding: "8px 12px",
    borderRadius: 6,
    backgroundColor: theme.palette.type === "dark" ? "rgba(2, 132, 199, 0.12)" : "#f0f9ff",
    borderLeft: "3px solid #0284c7",
  },
  boxAlert: {
    marginTop: 6,
    padding: "8px 12px",
    borderRadius: 6,
    backgroundColor: theme.palette.type === "dark" ? "rgba(225, 29, 72, 0.12)" : "#fff1f2",
    borderLeft: "3px solid #e11d48",
  },
  itemDivider: {
    marginTop: 12,
    marginBottom: 4,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
  },
}));

export default function PageHelpGuide(props) {
  const {
    helpData,
    tooltipText,
    ...restProps
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedSection, setExpandedSection] = useState("filtros");

  // Fallback robusto: si helpData no fue provisto pero se pasó el objeto mediante spread {...HELP_DATA}
  const effectiveHelpData =
    helpData ||
    (props.es || props.en || props.header || props.sections ? restProps : null);

  const currentLang = (i18n.language || "es").split("-")[0];
  const activeHelp =
    effectiveHelpData && effectiveHelpData[currentLang]
      ? effectiveHelpData[currentLang]
      : (effectiveHelpData && effectiveHelpData["es"])
      ? effectiveHelpData["es"]
      : effectiveHelpData;

  const header = activeHelp?.header || {};
  const sections = activeHelp?.sections || [];
  const finalTooltip = tooltipText || i18n.t("pageHelpGuide.tooltipDefault");

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleAccordionChange = (panelId) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? panelId : false);
  };

  // Filtrado reactivo en vivo
  const filteredSections = useMemo(() => {
    if (!search.trim()) return sections;
    const q = search.toLowerCase();

    return sections
      .map((sec) => {
        const matchesSection =
          sec.title.toLowerCase().includes(q) ||
          sec.description?.toLowerCase().includes(q);

        const matchingItems = sec.items.filter(
          (item) =>
            item.title.toLowerCase().includes(q) ||
            item.definition?.toLowerCase().includes(q) ||
            item.importance?.toLowerCase().includes(q) ||
            item.alert?.toLowerCase().includes(q)
        );

        if (matchesSection || matchingItems.length > 0) {
          return {
            ...sec,
            items: matchesSection ? sec.items : matchingItems,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [sections, search]);

  return (
    <>
      <div className={classes.sonarContainer}>
        <Tooltip title={finalTooltip} arrow placement="bottom">
          <IconButton
            className={classes.sonarBeacon}
            onClick={handleToggle}
            size="small"
            aria-label="Abrir ayuda contextual"
          >
            <HelpOutlineIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </div>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleToggle}
        classes={{ paper: classes.drawerPaper }}
      >
        {/* Cabecera del Drawer */}
        <div className={classes.drawerHeader}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              {(header.badge || i18n.t("pageHelpGuide.officialGuideBadge")) && (
                <Chip
                  icon={<MenuBookIcon style={{ fontSize: 14, color: "inherit" }} />}
                  label={header.badge || i18n.t("pageHelpGuide.officialGuideBadge")}
                  size="small"
                  className={classes.badgeChip}
                />
              )}
              <Typography variant="h6" className={classes.drawerTitle}>
                {header.title || i18n.t("pageHelpGuide.officialGuideBadge")}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleToggle}
              style={{
                color: theme.palette.type === "dark" ? "#94a3b8" : "#64748b",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </div>

        {/* Buscador de temas */}
        <div className={classes.searchBox}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={i18n.t("pageHelpGuide.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    style={{ color: theme.palette.text.secondary }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </div>

        {/* Contenido con scroll */}
        <div className={classes.drawerContent}>
          {header.summary && !search && (
            <div className={classes.summaryCard}>
              <Typography className={classes.summaryText}>
                {header.summary}
              </Typography>
            </div>
          )}

          {filteredSections.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography variant="body2" color="textSecondary">
                {i18n.t("pageHelpGuide.noMatches")} &quot;{search}&quot;.
              </Typography>
            </Box>
          ) : (
            filteredSections.map((sec, secIdx) => {
              const isExpanded =
                Boolean(search.trim()) || expandedSection === sec.id;

              return (
                <Accordion
                  key={sec.id || secIdx}
                  expanded={isExpanded}
                  onChange={handleAccordionChange(sec.id)}
                  classes={{
                    root: classes.accordionRoot,
                    expanded: classes.expanded,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    classes={{
                      root: classes.accordionSummary,
                      expanded: classes.expanded,
                    }}
                  >
                    {(() => {
                      const match = (sec.title || "").match(/^(\d+\.)\s*(.*)$/);
                      const numberPrefix = match ? match[1] : null;
                      const cleanTitle = match ? match[2] : sec.title;

                      return (
                        <Box
                          display="flex"
                          alignItems="flex-start"
                          style={{
                            width: "100%",
                            paddingRight: 6,
                          }}
                        >
                          <FiberManualRecordIcon
                            style={{
                              fontSize: 9,
                              color: theme.palette.primary.main,
                              marginTop: 4,
                              marginRight: 12,
                              flexShrink: 0,
                            }}
                          />
                          {numberPrefix && (
                            <Typography
                              component="span"
                              style={{
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                lineHeight: 1.35,
                                marginRight: 8,
                                flexShrink: 0,
                                color: theme.palette.primary.main,
                              }}
                            >
                              {numberPrefix}
                            </Typography>
                          )}
                          <Typography
                            component="span"
                            className={classes.sectionTitle}
                            style={{
                              flex: 1,
                              fontWeight: 600,
                              fontSize: "0.95rem",
                              lineHeight: 1.35,
                              color: theme.palette.type === "dark" ? "#f1f5f9" : "#1e293b",
                              textAlign: "left",
                            }}
                          >
                            {cleanTitle}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    {sec.description && (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{
                          fontStyle: "italic",
                          marginBottom: 8,
                          textAlign: "justify",
                        }}
                      >
                        {sec.description}
                      </Typography>
                    )}

                    {sec.items.map((item, itemIdx) => (
                      <React.Fragment key={item.id || itemIdx}>
                        <div className={classes.itemRow}>
                          {/* Columna 1: Icono estándar 32x32 alineado arriba */}
                          <div className={classes.iconBadge}>
                            <HelpOutlineIcon style={{ fontSize: 18 }} />
                          </div>

                          {/* Columna 2: Título, etiquetas y texto justificado en el mismo margen vertical */}
                          <div className={classes.textContentColumn}>
                            <Typography className={classes.itemTitle}>
                              {item.title}
                            </Typography>

                            {item.definition && (
                              <>
                                <Typography className={classes.labelTag}>
                                  {i18n.t("pageHelpGuide.definitionTag")}
                                </Typography>
                                <Typography className={classes.justifiedParagraph}>
                                  {item.definition}
                                </Typography>
                              </>
                            )}

                            {item.importance && (
                              <div className={classes.boxImportance}>
                                <Typography
                                  className={classes.labelTag}
                                  style={{ color: "#0284c7" }}
                                >
                                  {i18n.t("pageHelpGuide.importanceTag")}
                                </Typography>
                                <Typography
                                  className={classes.justifiedParagraph}
                                  style={{ marginBottom: 0 }}
                                >
                                  {item.importance}
                                </Typography>
                              </div>
                            )}

                            {item.alert && (
                              <div className={classes.boxAlert}>
                                <Typography
                                  className={classes.labelTag}
                                  style={{ color: "#e11d48" }}
                                >
                                  {i18n.t("pageHelpGuide.alertTag")}
                                </Typography>
                                <Typography
                                  className={classes.justifiedParagraph}
                                  style={{ marginBottom: 0 }}
                                >
                                  {item.alert}
                                </Typography>
                              </div>
                            )}
                          </div>
                        </div>

                        {itemIdx < sec.items.length - 1 && (
                          <Divider className={classes.itemDivider} />
                        )}
                      </React.Fragment>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </div>
      </Drawer>
    </>
  );
}
